import type { LayoutInput, LayoutResult, Force2Options } from '../types';
import { FORCE2_DEFAULTS } from '../defaults';
import { shapeBounds, shapeCenter } from '../utils/geometry';

/**
 * Fruchterman-Reingold inspired force layout.
 *
 * Key differences from `force`:
 *   - FR force model: repulsion ∝ k²/d, attraction ∝ d²/k
 *   - Temperature-based cooling: max displacement shrinks linearly
 *   - Adaptive ideal spacing (k) derived from drawing area and node count
 *   - Degree-weighted repulsion so hub nodes spread out more
 *   - Continuous gravity toward live centroid
 *   - No soft overlap/edge-cross forces during simulation — relies on
 *     the FR model to spread nodes, then hard overlap resolution after.
 */

interface ForceNode {
	id: string;
	x: number;
	y: number;
	width: number;
	height: number;
	dx: number;
	dy: number;
	fixed: boolean;
	degree: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function rectOverlap(
	ax: number, ay: number, aw: number, ah: number,
	bx: number, by: number, bw: number, bh: number,
	padding: number = 0,
): { ox: number; oy: number } {
	const ox = (aw / 2 + bw / 2 + padding) - Math.abs(ax - bx);
	const oy = (ah / 2 + bh / 2 + padding) - Math.abs(ay - by);
	if (ox <= 0 || oy <= 0) return { ox: 0, oy: 0 };
	return { ox, oy };
}

// ---------------------------------------------------------------------------
// Main algorithm
// ---------------------------------------------------------------------------

export function force2(
	input: LayoutInput,
	options?: Partial<Force2Options>,
): LayoutResult {
	const opts: Force2Options = { ...FORCE2_DEFAULTS, ...options };

	if (input.nodes.length === 0) return { nodes: [] };

	const iter = force2Iterator(input, opts);
	let result: LayoutResult = { nodes: [] };

	for (const frame of iter) {
		result = frame;
	}

	// Hard overlap resolution (same strategy as force)
	if (opts.minDistance >= 0 && result.nodes.length > 1) {
		resolveResultOverlaps(result, input, opts);
	}

	return result;
}

/**
 * Incremental iterator for live preview.
 */
export function* force2Iterator(
	input: LayoutInput,
	options?: Partial<Force2Options>,
): Generator<LayoutResult> {
	const opts: Force2Options = { ...FORCE2_DEFAULTS, ...options };
	const { nodes, edges, obstacles = [] } = input;

	if (nodes.length === 0) {
		yield { nodes: [] };
		return;
	}

	// --- Build adjacency for degree computation ---
	const degreeMap = new Map<string, number>();
	const allEdges = [...edges, ...(input.obstacleEdges ?? [])];
	for (const e of allEdges) {
		degreeMap.set(e.source, (degreeMap.get(e.source) ?? 0) + 1);
		degreeMap.set(e.target, (degreeMap.get(e.target) ?? 0) + 1);
	}

	// --- Initialize force nodes ---
	const forceNodes: ForceNode[] = nodes.map((n) => {
		const b = shapeBounds(n.shape);
		const c = shapeCenter(n.shape);
		return {
			id: n.id,
			x: c.x,
			y: c.y,
			width: b.width,
			height: b.height,
			dx: 0,
			dy: 0,
			fixed: false,
			degree: degreeMap.get(n.id) ?? 0,
		};
	});

	const obstacleNodes: ForceNode[] = obstacles.map((n) => {
		const b = shapeBounds(n.shape);
		const c = shapeCenter(n.shape);
		return {
			id: n.id,
			x: c.x,
			y: c.y,
			width: b.width,
			height: b.height,
			dx: 0,
			dy: 0,
			fixed: true,
			degree: degreeMap.get(n.id) ?? 0,
		};
	});

	// --- Pre-position cluster outside obstacles (same logic as force) ---
	if (obstacleNodes.length > 0 && input.obstacleEdges && input.obstacleEdges.length > 0) {
		const targetIds = new Set(forceNodes.map((n) => n.id));
		const obsIndex = new Map(obstacleNodes.map((n) => [n.id, n]));

		const anchorPositions: { x: number; y: number }[] = [];
		for (const e of input.obstacleEdges) {
			const obsNode =
				(targetIds.has(e.source) && obsIndex.get(e.target)) ||
				(targetIds.has(e.target) && obsIndex.get(e.source));
			if (obsNode) {
				anchorPositions.push({ x: obsNode.x, y: obsNode.y });
			}
		}

		if (anchorPositions.length > 0) {
			const anchorCx = anchorPositions.reduce((s, p) => s + p.x, 0) / anchorPositions.length;
			const anchorCy = anchorPositions.reduce((s, p) => s + p.y, 0) / anchorPositions.length;

			let obMinX = Infinity, obMinY = Infinity, obMaxX = -Infinity, obMaxY = -Infinity;
			for (const n of obstacleNodes) {
				obMinX = Math.min(obMinX, n.x - n.width / 2);
				obMinY = Math.min(obMinY, n.y - n.height / 2);
				obMaxX = Math.max(obMaxX, n.x + n.width / 2);
				obMaxY = Math.max(obMaxY, n.y + n.height / 2);
			}

			const gap = 30;
			const distLeft = anchorCx - obMinX;
			const distRight = obMaxX - anchorCx;
			const distTop = anchorCy - obMinY;
			const distBottom = obMaxY - anchorCy;
			const minDist = Math.min(distLeft, distRight, distTop, distBottom);

			let targetX: number, targetY: number;
			if (minDist === distRight) {
				targetX = obMaxX + gap; targetY = anchorCy;
			} else if (minDist === distLeft) {
				targetX = obMinX - gap; targetY = anchorCy;
			} else if (minDist === distBottom) {
				targetX = anchorCx; targetY = obMaxY + gap;
			} else {
				targetX = anchorCx; targetY = obMinY - gap;
			}

			const clusterCx = forceNodes.reduce((s, n) => s + n.x, 0) / forceNodes.length;
			const clusterCy = forceNodes.reduce((s, n) => s + n.y, 0) / forceNodes.length;
			const shiftX = targetX - clusterCx;
			const shiftY = targetY - clusterCy;
			for (const n of forceNodes) {
				n.x += shiftX;
				n.y += shiftY;
			}
		}
	}

	const allNodes = [...forceNodes, ...(opts.avoidObstacles ? obstacleNodes : [])];
	const allNodeIndex = new Map(allNodes.map((n, i) => [n.id, i]));

	// Edge pairs (indices into allNodes) — at least one endpoint movable
	const edgePairs: [number, number][] = [];
	for (const e of allEdges) {
		const si = allNodeIndex.get(e.source);
		const ti = allNodeIndex.get(e.target);
		if (si !== undefined && ti !== undefined) {
			if (!allNodes[si].fixed || !allNodes[ti].fixed) {
				edgePairs.push([si, ti]);
			}
		}
	}

	// --- Compute ideal spacing k ---
	// Use total node area (invariant to layout) instead of bounding box
	// to prevent feedback loop: re-running on spread-out output won't
	// inflate k since node sizes don't change.
	let totalNodeArea = 0;
	for (const n of forceNodes) {
		totalNodeArea += n.width * n.height;
	}
	// Average node diagonal — used for temperature scaling
	const avgDiag = forceNodes.reduce(
		(s, n) => s + Math.sqrt(n.width * n.width + n.height * n.height), 0,
	) / forceNodes.length;

	const k = opts.spacing * Math.sqrt(totalNodeArea / forceNodes.length);
	const k2 = k * k;

	// Initial temperature based on average node size (not bounding box)
	// so it's stable across re-runs regardless of initial spread.
	const t0 = opts.temperature * avgDiag * Math.sqrt(forceNodes.length);

	// --- Simulation loop ---
	for (let iter = 0; iter < opts.iterations; iter++) {
		const progress = iter / opts.iterations;
		// Linear temperature decay
		const temp = t0 * (1 - progress);

		// Reset displacements
		for (const n of forceNodes) {
			n.dx = 0;
			n.dy = 0;
		}

		// =================================================================
		// Repulsion: FR model — f_rep = k² / d
		// Degree weighting: high-degree nodes repel more
		// =================================================================
		for (let i = 0; i < allNodes.length; i++) {
			for (let j = i + 1; j < allNodes.length; j++) {
				const a = allNodes[i];
				const b = allNodes[j];
				const dx = b.x - a.x;
				const dy = b.y - a.y;
				const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;

				// Degree-weighted repulsion
				const degA = 1 + opts.degreeRepulsion * a.degree;
				const degB = 1 + opts.degreeRepulsion * b.degree;
				const weight = (degA + degB) / 2;

				const force = weight * k2 / dist;
				const fx = (dx / dist) * force;
				const fy = (dy / dist) * force;

				if (!a.fixed) {
					a.dx -= fx;
					a.dy -= fy;
				}
				if (!b.fixed) {
					b.dx += fx;
					b.dy += fy;
				}
			}
		}

		// =================================================================
		// Attraction: FR model — f_att = d² / k
		// =================================================================
		for (const [si, ti] of edgePairs) {
			const a = allNodes[si];
			const b = allNodes[ti];
			const dx = b.x - a.x;
			const dy = b.y - a.y;
			const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;

			const force = opts.attraction * (dist * dist) / k;
			const fx = (dx / dist) * force;
			const fy = (dy / dist) * force;

			if (!a.fixed) {
				a.dx += fx;
				a.dy += fy;
			}
			if (!b.fixed) {
				b.dx -= fx;
				b.dy -= fy;
			}
		}

		// =================================================================
		// Gravity: pull toward live centroid
		// =================================================================
		if (opts.gravity > 0) {
			let cx = 0, cy = 0;
			for (const n of forceNodes) {
				cx += n.x;
				cy += n.y;
			}
			cx /= forceNodes.length;
			cy /= forceNodes.length;

			for (const n of forceNodes) {
				const dx = cx - n.x;
				const dy = cy - n.y;
				const dist = Math.sqrt(dx * dx + dy * dy) || 0.01;
				// Gravity proportional to distance from center
				n.dx += dx * opts.gravity;
				n.dy += dy * opts.gravity;
			}
		}

		// =================================================================
		// Apply displacements, clamped by temperature
		// =================================================================
		let maxMovement = 0;
		for (const n of forceNodes) {
			const disp = Math.sqrt(n.dx * n.dx + n.dy * n.dy) || 0.01;

			// Clamp displacement to current temperature
			const capped = Math.min(disp, temp);
			const scale = capped / disp;

			n.x += n.dx * scale;
			n.y += n.dy * scale;
			maxMovement = Math.max(maxMovement, capped);
		}

		// Yield current state
		yield {
			nodes: forceNodes.map((n) => ({
				id: n.id,
				x: n.x - n.width / 2,
				y: n.y - n.height / 2,
			})),
		};

		// Convergence check
		if (opts.mode === 'converge' && maxMovement < opts.threshold) {
			return;
		}
	}
}

// ---------------------------------------------------------------------------
// Post-process overlap resolution
// ---------------------------------------------------------------------------

function resolveResultOverlaps(
	result: LayoutResult,
	input: LayoutInput,
	opts: Force2Options,
): void {
	const nodeMap = new Map(input.nodes.map((n) => [n.id, n]));
	const forceNodes: ForceNode[] = result.nodes.map((n) => {
		const src = nodeMap.get(n.id);
		const w = n.width ?? (src ? shapeBounds(src.shape).width : 0);
		const h = n.height ?? (src ? shapeBounds(src.shape).height : 0);
		return {
			id: n.id,
			x: n.x + w / 2,
			y: n.y + h / 2,
			width: w,
			height: h,
			dx: 0, dy: 0,
			fixed: false,
			degree: 0,
		};
	});

	if (opts.avoidObstacles && input.obstacles) {
		for (const obs of input.obstacles) {
			const b = shapeBounds(obs.shape);
			const c = shapeCenter(obs.shape);
			forceNodes.push({
				id: obs.id,
				x: c.x, y: c.y,
				width: b.width, height: b.height,
				dx: 0, dy: 0,
				fixed: true,
				degree: 0,
			});
		}
	}

	const maxPasses = forceNodes.length * 2;
	for (let pass = 0; pass < maxPasses; pass++) {
		let anyOverlap = false;
		for (let i = 0; i < forceNodes.length; i++) {
			for (let j = i + 1; j < forceNodes.length; j++) {
				const a = forceNodes[i];
				const b = forceNodes[j];
				const { ox, oy } = rectOverlap(
					a.x, a.y, a.width, a.height,
					b.x, b.y, b.width, b.height,
					opts.minDistance,
				);
				if (ox <= 0 || oy <= 0) continue;

				anyOverlap = true;
				const bothMovable = !a.fixed && !b.fixed;
				const half = bothMovable ? 0.5 : 1;
				if (ox < oy) {
					const sign = b.x >= a.x ? 1 : -1;
					const shift = (ox + 0.5) * half;
					if (!a.fixed) a.x -= sign * shift;
					if (!b.fixed) b.x += sign * shift;
				} else {
					const sign = b.y >= a.y ? 1 : -1;
					const shift = (oy + 0.5) * half;
					if (!a.fixed) a.y -= sign * shift;
					if (!b.fixed) b.y += sign * shift;
				}
			}
		}
		if (!anyOverlap) break;
	}

	for (const fn of forceNodes) {
		if (fn.fixed) continue;
		const rn = result.nodes.find((n) => n.id === fn.id);
		if (rn) {
			rn.x = fn.x - fn.width / 2;
			rn.y = fn.y - fn.height / 2;
		}
	}
}
